/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Component,
  computed,
  effect,
  inject,
  input,
  isDevMode,
  OnInit,
  output,
  signal,
  untracked,
} from "@angular/core";
import { FaIconLibrary, FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faAngleDown, faAngleLeft, faAngleRight, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { NgbModal, NgbPaginationModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import {
  DndDraggableDirective,
  DndDropEvent,
  DndDropzoneDirective,
  DndModule,
  DndPlaceholderRefDirective,
} from "ngx-drag-drop";
import { ToastrService } from "ngx-toastr";

import { PermissionActionDescriptorType } from "../../../../../adaa-types";
import { AdaaHelper } from "../../../../core/utils";
import { Language, PermissionAction } from "../../../constants/enums";
import { ResizeColumnDirective, SmartToolTipsDirective, SortableHeaderDirective, SortEvent } from "../../../directives";
import {
  CustomButton,
  CustomTableButton,
  FormCatalog,
  ItemAction,
  MainResponseModel,
  ParameterCatalog,
  RowWithIssueType,
  SearchFields,
  TableActionEvent,
  TableInputChangedOutput,
  TableInputs,
  TableSelectedRow,
  TableValueListOutput,
  TotalRows,
  ValueText,
} from "../../../models";
import {
  AppService,
  DataTableDataService,
  DataTableExportService,
  DataTableService,
  LanguageService,
} from "../../../services";
import { FormDropdownComponent } from "../../form";
import { DataTableActionsComponent } from "../data-table-actions/data-table-actions";
import { DataTableButtonsComponent } from "../data-table-buttons/data-table-buttons";
import { DataTableCellComponent } from "../data-table-cell/data-table-cell";
import { DataTableFiltersComponent } from "../data-table-filters/data-table-filters";
import { DataTableSettingsComponent } from "../data-table-settings/data-table-settings";

@Component({
  selector: "adaa-data-table",
  imports: [
    TranslatePipe,
    DndModule,
    DndDropzoneDirective,
    DndDraggableDirective,
    DataTableCellComponent,
    DataTableActionsComponent,
    DataTableButtonsComponent,
    DndPlaceholderRefDirective,
    ResizeColumnDirective,
    SortableHeaderDirective,
    NgbPaginationModule,
    FontAwesomeModule,
    SmartToolTipsDirective,
    FormDropdownComponent,
  ],
  templateUrl: "./data-table.html",
  styleUrl: "./data-table.scss",
})
export class DataTableComponent implements OnInit {
  // Table display inputs
  titleHeader = input<string>();
  useSmHeader = input<boolean>(false);
  required = input<boolean>(false);
  tableClasses = input<string>("table-striped table-hover");
  disablePagination = input<boolean>(false);

  // Toolbar button inputs
  enableAdd = input<boolean>(true);
  enablePrint = input<boolean>(false);
  enableSettings = input<boolean>(true);
  enableFilter = input<boolean>(true);
  enableExport = input<boolean>(true);
  customTableButtons = input<CustomTableButton[]>([]);

  // Actions cell inputs
  enableActionsCell = input<boolean>(true); // To show or hide actions cell in the table
  enableConnectionsAction = input<boolean>(false);
  enableViewAction = input<boolean>(true);
  enableEditAction = input<boolean>(true);
  enableDeleteAction = input<boolean>(true);
  customActionButtons = input<CustomButton[]>([]);

  // Rules inputs
  disableViewRules = input<ItemAction[]>([]);
  disableEditRules = input<ItemAction[]>([]);
  disableConnectionsRules = input<ItemAction[]>([]);
  disableDeleteRules = input<ItemAction[]>([]);
  hideViewRules = input<ItemAction[]>([]);
  hideEditRules = input<ItemAction[]>([]);
  hideConnectionsRules = input<ItemAction[]>([]);
  hideDeleteRules = input<ItemAction[]>([]);
  hideChildViewRules = input<ItemAction[]>([]);
  hideChildEditRules = input<ItemAction[]>([]);
  hideChildConnectionsRules = input<ItemAction[]>([]);
  hideChildDeleteRules = input<ItemAction[]>([]);
  disableChildEditRules = input<ItemAction[]>([]);

  // API / Data inputs
  isLocalData = input<boolean>(false);
  configKey = input<string>(); // The search key that we have to get the config list from (e.g. nkpis_conf_list) Important for filter
  apiPrefix = input<string>(); // To put a prefix before the main call (e.g. if prefix = 'pillar' then => "pillar/getallv2...")
  apiName = input<string>("getAllV2"); // You can use this instead of apiPrefix and add the apiName to data table data service
  paramsString = input<string>(); // to pass url params example "userId=123&entityId=2"
  loader = input<boolean>(true);
  tableInputs = input<TableInputs[]>([]);

  // Prevents pagination from resetting on every data update (useful for local table data with inputs)
  resetPaginationOnDataUpdate = input<boolean>(true);

  // localData replaces the old @Input() setter — reacts via effect in constructor
  localData = input<object[]>([]);

  // itemTypeId replaces the old @Input() setter — reacts via effect in constructor
  itemTypeId = input<number | undefined>(undefined);

  // Selection inputs
  childItemTypeId = input<number | undefined>(undefined); // Used for the child table if you have a table with children and you want to control the permissions of the child table based on the item type id
  enableSelection = input<boolean>(false); // To allow selection in the table
  multiSelection = input<any[] | undefined>(undefined); // To allow multi selection in the table (set to empty array to enable, undefined to disable)
  rowSelected = input<number | undefined>(undefined); // Initial selected row id
  notAllowedToSelect = input<ItemAction[]>([]);
  selectionId = input<string>("id");

  //Used if table has children
  tableWithChildren = input<boolean>(false);
  childKey = input<string>("children");
  enableChildActionsCell = input<boolean>(false);
  enableEditChildAction = input<boolean>(false);
  enableViewChildAction = input<boolean>(false);
  enableDeleteChildAction = input<boolean>(false);
  enableConnectionsChildAction = input<boolean>(false);

  // Other already-signal inputs
  disableTableActions = input<boolean>(false);
  rowsWithIssues = input<RowWithIssueType[]>([]);
  staticSearchFields = input<SearchFields>({ searchFields: {} }); // *note: Used when the parent component wants filtered data on page initialization
  alwaysFilterBySearchFields = input<SearchFields>({ searchFields: {} }); // *note: These filters will always be called regardless of the current selected filters

  // Outputs
  inputListChanged = output<TableValueListOutput[]>();
  addNewClicked = output<void>();
  buttonClicked = output<string>();
  actionClicked = output<TableActionEvent>();
  childClicked = output<TableActionEvent>();
  getRowData = output<TableSelectedRow>(); // To send row information if clicked (enableSelection should be true)
  tableDataChanged = output<any[]>();

  private readonly _dataTableService = inject(DataTableService);
  private readonly _dataTableDataService = inject(DataTableDataService);
  private readonly _dataTableExportService = inject(DataTableExportService);
  private readonly _appService = inject(AppService);
  private readonly _iconLibrary = inject(FaIconLibrary);
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _modalService = inject(NgbModal);
  readonly languageService = inject(LanguageService);

  private readonly _basicPermission: PermissionActionDescriptorType = {
    actor: {
      modifier: "and",
      permissions: [],
    },
  };

  private _itemId = signal<number | undefined>(undefined);
  private _originalColumns = signal<string[]>([]); // To have all columns without any conditions.
  private _displayedColumns = signal<string[]>([]); // To have all columns that should be displayed based on special conditions.
  private _paramsStringInitialized = false;

  formCatalog = signal<FormCatalog | undefined>(undefined);
  isMobile = AdaaHelper.isMobile();
  limit = signal<number>(this.isMobile ? 5 : 10);
  AdaaHelper = AdaaHelper;
  Language = Language;
  tableColumns = signal<ParameterCatalog[]>([]);
  offset = signal<number>(0);
  currentPage = signal<number>(1);
  totalRows = signal<number>(0);
  searchFields = signal<SearchFields>({ searchFields: {} });
  currentSort = signal<SortEvent | undefined>(undefined);
  currentFilter = signal<any>(null);
  resizingInProgress = signal<boolean>(false);
  generatedID = Math.random().toString();
  tableData = signal<any[]>([]);
  tableDataCopy = signal<any[]>([]);
  tableDataFiltered = signal<any[]>([]);
  inputValues = signal<TableValueListOutput[]>([]);
  isNotArchived = signal<boolean>(true);
  innerWidth = window.innerWidth;
  limitOptions: ValueText[] = [
    { value: 5, text: "5" },
    { value: 10, text: "10" },
    { value: 25, text: "25" },
    { value: 50, text: "50" },
  ];

  // Used for permissions
  createPermission = signal<PermissionActionDescriptorType>(this._basicPermission);
  managePermission = signal<PermissionActionDescriptorType>(this._basicPermission);
  viewPermission = signal<PermissionActionDescriptorType>(this._basicPermission);

  //child permissions
  childManagePermission = signal<PermissionActionDescriptorType>(this._basicPermission);
  childViewPermission = signal<PermissionActionDescriptorType>(this._basicPermission);

  // Internal mutable state for selection (signal inputs are read-only)
  private _rowSelected = signal<number | undefined>(undefined);
  private _multiSelection = signal<any[] | undefined>(undefined);

  // Computed state key for table state persistence
  private _stateConfigKey = computed(() => `${this.configKey() ?? ""}_${this._itemId() ?? ""}`);

  isFilterApplied = computed(() =>
    Object.values(this.searchFields().searchFields).some(
      (e) => (e?.lov && (e?.lov as object[]).length > 0) || e?.range != undefined || e?.value != undefined
    )
  );

  totalPages = computed(() => this.totalRows() / this.limit());

  constructor() {
    // Language change → re-check config
    // The body reads and writes internal state signals, so it runs untracked to keep
    // `language()` as the only dependency and avoid an effect feedback loop.
    effect(() => {
      const lang = this._appService.language();
      untracked(() => this._checkConfig(lang));
    });

    // localData input → sync internal data state (replaces the old @Input() setter)
    effect(() => {
      const value = this.localData();

      untracked(() => {
        if (!value || AdaaHelper.isSameArray(value, this.tableDataCopy())) return;

        const forceReset = this.tableDataCopy()?.length !== value?.length;

        this.tableDataCopy.set([...value]);
        this.tableDataFiltered.set([...value]);
        this.totalRows.set(value.length);

        if (this.resetPaginationOnDataUpdate() || forceReset) {
          this.currentPage.set(1);
          this._applyLocalFilter();
          this._doPagination();
        } else {
          this._doPagination();
        }
      });
    });

    // itemTypeId input → update permission signals (replaces the old @Input() setter)
    effect(() => {
      const data = this.itemTypeId();
      this._itemId.set(data);
      if (!data) return;

      this.createPermission.set({
        actor: {
          modifier: "and",
          permissions: [
            {
              permissionAction: PermissionAction.create,
              objectTypeId: data,
            },
          ],
        },
      });

      this.managePermission.set({
        actor: {
          modifier: "and",
          permissions: [
            {
              permissionAction: PermissionAction.manage,
              objectTypeId: data,
            },
          ],
        },
      });

      this.viewPermission.set({
        actor: {
          modifier: "and",
          permissions: [
            {
              permissionAction: PermissionAction.view,
              objectTypeId: data,
            },
          ],
        },
      });

      this.isNotArchived.set(this._dataTableService.checkIfNotArchived(data));
    });

    //Used for the table Permission just pass the value and table will hide the create/delete/manage/view buttons
    //This is used for the child table if you have a table with children and you want to control the permissions of the child table based on the item type id
    //You need to use Constants.CONSTANT_PERMISSIONS
    effect(() => {
      const data = this.childItemTypeId();

      if (!data) return;

      this.childManagePermission.set({
        actor: {
          modifier: "and",
          permissions: [
            {
              permissionAction: PermissionAction.manage,
              objectTypeId: data,
            },
          ],
        },
      });

      this.childViewPermission.set({
        actor: {
          modifier: "and",
          permissions: [
            {
              permissionAction: PermissionAction.view,
              objectTypeId: data,
            },
          ],
        },
      });
    });

    // configKey input → reload config (replaces ngOnChanges configKey check)
    effect(() => {
      const key = this.configKey();
      if (key) untracked(() => this._getConfig());
    });

    // paramsString input → reset filters on subsequent changes (replaces ngOnChanges paramsString check)
    effect(() => {
      this.paramsString(); // subscribe to signal changes
      if (!this._paramsStringInitialized) {
        this._paramsStringInitialized = true;
        return;
      }
      untracked(() => this.resetFilters());
    });

    // Sync rowSelected input → internal mutable signal
    effect(() => {
      this._rowSelected.set(this.rowSelected());
    });

    // Sync multiSelection input → internal mutable signal
    effect(() => {
      this._multiSelection.set(this.multiSelection());
    });
  }

  public ngOnInit(): void {
    this._iconLibrary.addIcons(faSortUp, faSortDown, faAngleLeft, faAngleRight, faAngleDown);
  }

  public onSort(event: SortEvent): void {
    this.currentSort.set(event);
    if (this.isLocalData()) this._localSort(event);
    else {
      this.searchFields.set(
        this._dataTableService.getSortAndFilterData(this.tableColumns(), this.currentFilter(), this.currentSort())
      );
      this._setTableState();
      this._getTableData(
        this.apiName(),
        this.limit(),
        this.offset(),
        this.apiPrefix(),
        this.paramsString(),
        this.loader(),
        this.searchFields()
      );
    }
  }

  public onDrop(event: DndDropEvent): void {
    if (event.index === undefined) return;

    const columns = [...this.tableColumns()];
    const index = columns.findIndex((e) => e.id === event.data.id);
    if (index != -1) {
      columns.splice(index, 1);
      columns.splice(event.index, 0, event.data);
      this.tableColumns.set(columns);
      this._saveTableSettings(true);
    }
  }

  public onPageChange(event: any): void {
    const target = event.target as HTMLElement;
    const pageItem = target.closest(".page-item"); // Get closest .page-item
    const anchor = target.closest("a"); // Get clicked <a> inside .page-item

    if (!pageItem || !anchor) return;

    const isDisabled = pageItem.classList.contains("disabled");
    const ariaLabel = anchor.getAttribute("aria-label");
    if (ariaLabel !== "Next" && ariaLabel !== "Previous" && isDisabled) return;

    // Handling "Next" button
    if (ariaLabel === "Next") {
      if (this.currentPage() < this.totalRows()) this.currentPage.update((page) => page + 1); // Navigate to next page
    }
    // Handling "Previous" button
    else if (ariaLabel === "Previous") {
      if (this.currentPage() > 1) this.currentPage.update((page) => page - 1); // Navigate to previous page
    }
    // Handling direct page clicks
    else {
      const pageNumber = Number(anchor.textContent?.trim());
      if (!isNaN(pageNumber)) {
        this.currentPage.set(pageNumber); // Handle direct number clicks
      }
    }

    this._doPagination();
  }

  /**
   *
   * @param resetPagination default: false - reset table pagination
   * @param loader default false - show/hide loader
   */
  public loadTableData(resetPagination?: boolean, loader?: boolean) {
    if (resetPagination) {
      this.offset.set(0);
      this.currentPage.set(1);
    }

    this._getTableData(
      this.apiName(),
      this.limit(),
      this.offset(),
      this.apiPrefix(),
      this.paramsString(),
      loader ?? this.loader(),
      this.searchFields()
    );
  }

  public printClicked(event: boolean): void {
    if (!event) return;

    this._dataTableService.printTable(this.generatedID, this.titleHeader() ?? "");
  }

  public filterClicked(event: boolean): void {
    if (!event) return;

    const modal = this._modalService.open(DataTableFiltersComponent, {
      centered: true,
      size: "xl",
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.componentInstance.configKey.set(this.configKey());
    modal.componentInstance.tableColumns.set(this.tableColumns());
    modal.componentInstance.filterValues.set(this.searchFields());

    modal.result.then((e) => {
      if (e === true) {
        this.resetFilters();
      } else if (e === undefined) {
        return;
      } else {
        this.isLocalData() ? this._localFilter(e) : this._applyFilters(e);
      }
    });
  }

  public settingsClicked(event: boolean): void {
    if (!event) return;

    const modal = this._modalService.open(DataTableSettingsComponent, {
      centered: true,
      size: "lg",
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modal.componentInstance.tableColumns.set(
      (this.formCatalog()?.parameterCatalogs ?? [])
        .filter((e) => {
          const hasStep = e.step === this._appService.language() || !e.step;
          const hasLang = e.lang === this._appService.language() || !e.lang;
          const isVisible = e.visibleParameter === "Y";

          return isVisible && hasStep && hasLang;
        })
        .sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1))
    );

    modal.result.then((e) => {
      if (e === undefined) {
        return;
      } else {
        this._applySettings(e);
      }
    });
  }

  public exportClicked(event: boolean): void {
    if (!event) return;

    if (this.isLocalData())
      this._dataTableExportService.exportTableData(
        this.formCatalog()?.searchKey,
        this._displayedColumns(),
        this.formCatalog()?.parameterCatalogs ?? [],
        this.tableDataCopy()
      );
    else this._exportTable(this.apiName(), this.apiPrefix(), this.paramsString(), this.loader());
  }

  public columnResized(width: number, column: ParameterCatalog) {
    column.width = width;
    const columns = [...this.tableColumns()];
    const index = columns.findIndex((e) => e.id === column.id);
    if (index != -1) {
      columns[index] = column;
      this.tableColumns.set(columns);
      this._saveTableSettings();
    }
  }

  public resetFilters(): void {
    this.currentFilter.set(null);
    this.currentPage.set(1);
    this.offset.set(this.limit() * (this.currentPage() - 1));

    this._setTableState();
    this.searchFields.set(
      this._dataTableService.getSortAndFilterData(this.tableColumns(), this.currentFilter(), this.currentSort())
    );

    if (this.isLocalData()) {
      this._applyLocalFilter();
    } else
      this._getTableData(
        this.apiName(),
        this.limit(),
        this.offset(),
        this.apiPrefix(),
        this.paramsString(),
        this.loader(),
        this.searchFields()
      );
  }

  public rowClicked(row: any): void {
    if (!this.enableSelection()) return;
    let bypass = true;

    for (let index = 0; index < this.notAllowedToSelect().length; index++) {
      const rule = this.notAllowedToSelect()[index];

      if (rule.operator === "equal") {
        if (row[rule.propertyName] === rule.compareWith) {
          bypass = false;
        }
      } else if (rule.operator === "not-equal") {
        bypass = false;
        if (row[rule.propertyName] === rule.compareWith) {
          bypass = true;
          break;
        }
      } else if (rule.operator === "notNull") {
        bypass = !AdaaHelper.isDefined(row[rule.propertyName]);
      } else if (rule.operator === "null") {
        bypass = !!AdaaHelper.isDefined(row[rule.propertyName]);
      }
    }

    if (!bypass) return;

    this._rowSelected.set(row[this.selectionId()]);

    const currentMultiSelection = this._multiSelection();
    if (currentMultiSelection) {
      if (currentMultiSelection.findIndex((a) => a[this.selectionId()] === row[this.selectionId()]) === -1) {
        this._multiSelection.set([...currentMultiSelection, row]);
      } else {
        this._multiSelection.set(
          currentMultiSelection.filter((a) => a[this.selectionId()] !== row[this.selectionId()])
        );
      }
    }

    this.getRowData.emit({
      item: row,
      allSelectedRows: this._multiSelection() ?? [],
    });
  }

  public isRowSelected(row: any): boolean {
    if (this.enableSelection() && !this._multiSelection()) {
      return row[this.selectionId()] === this._rowSelected();
    }
    if (this._multiSelection()) {
      return this._multiSelection()!.filter((a) => a[this.selectionId()] === row[this.selectionId()]).length > 0;
    }
    return false;
  }

  public rowHasIssue(data: any, $index: number) {
    if (this.rowsWithIssues().length === 0) return false;

    const issue = this.rowsWithIssues().find((row) => {
      if (row.use === "id" && row.propName) return data[row.propName] === row.value;
      if (row.use === "index") return $index === row.value;
      return false;
    });

    return !!issue;
  }

  public inputChanged(event: TableInputChangedOutput) {
    // Persist the new value into the row model so pagination / rerendering keeps the checkbox state.
    // We try to locate the row in the backing arrays via `selectionId` (fallback to `id`).
    const keyField = this.selectionId() || "id";
    const rowKey = event?.data?.[keyField] ?? event?.data?.id;
    if (rowKey !== undefined && rowKey !== null) {
      const applyTo = (rows: any[]) => {
        const idx = rows.findIndex((r) => (r?.[keyField] ?? r?.id) === rowKey);
        if (idx !== -1) {
          rows[idx][event.fieldName] = event.value;
        }
      };

      applyTo(this.tableData());
      applyTo(this.tableDataCopy());
      applyTo(this.tableDataFiltered());

      // For local tables we paginate from `tableDataFiltered`, so refresh the current page slice.
      if (this.isLocalData()) {
        this._doPagination();
      }
    } else if (event?.data && event.fieldName) {
      // Fallback: update the current row reference only (best-effort).
      event.data[event.fieldName] = event.value;
    }

    const values = [...this.inputValues()];
    const index = values.findIndex(
      (e) => (e?.data?.[keyField] ?? e?.data?.id) === rowKey && e.fieldName === event.fieldName
    );
    if (index === -1) {
      values.push(event);
    } else values[index] = event;

    this.inputValues.set(values);
    this.inputListChanged.emit(values);
  }

  public expandCollapseChild(row: any, isexpanded: boolean) {
    row["expanded"] = isexpanded;
  }

  public limitChanged(value: number) {
    this.limit.set(value);
    this.currentPage.set(1);
    this.offset.set(this.limit() * (this.currentPage() - 1));
    this._doPagination();
  }

  private _getConfig(): void {
    this._dataTableService.getConfig(this.configKey()!).subscribe((response: MainResponseModel) => {
      this.formCatalog.set(response.responseData as FormCatalog);
      this._checkConfig(this._appService.language());
      this._checkTableState();
    });
  }

  /**
   * @description To have a proper displayColumns and originalColumns so we can show in settings
   */
  private _checkConfig(lang: Language) {
    const formCatalog = this.formCatalog();
    if (!AdaaHelper.isDefined(formCatalog)) return;

    this._displayedColumns.set(
      this._dataTableService.getTableSettings(
        "displayColumns",
        formCatalog!.parameterCatalogs,
        this.enableActionsCell(),
        lang
      )
    );
    this._originalColumns.set(
      this._dataTableService.getTableSettings(
        "originalColumns",
        formCatalog!.parameterCatalogs,
        this.enableActionsCell(),
        lang
      )
    );
    this.currentSort.set(this._dataTableService.getDefaultSort(formCatalog!.parameterCatalogs, lang));

    this._updateTableColumns();
  }

  /**
   * @description Show/Hide table columns
   */
  private _updateTableColumns(): void {
    const columns = (this.formCatalog()?.parameterCatalogs ?? [])
      .filter((e) => this._displayedColumns().includes(e.fieldName))
      .sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1));

    this.tableColumns.set(this._dataTableService.checkColumnsForFilterModal(columns, this.configKey()!));
  }

  private _applySettings(event: ParameterCatalog[]): void {
    this.tableColumns.set(event);
    this._saveTableSettings(true);
  }

  private _applyFilters(event: any): void {
    this.currentFilter.set(event);
    this.searchFields.set(
      this._dataTableService.getSortAndFilterData(this.tableColumns(), this.currentFilter(), this.currentSort())
    );

    //Issue fix
    //After applying the filters we need to reset the table to the first page
    this.currentPage.set(1);
    this.offset.set(this.limit() * (this.currentPage() - 1));

    this._setTableState();
    this._getTableData(
      this.apiName(),
      this.limit(),
      this.offset(),
      this.apiPrefix(),
      this.paramsString(),
      this.loader(),
      this.searchFields()
    );
  }

  /**
   * @param apiName
   * @param limit
   * @param offset
   * @param [prefix="item"]
   * @param paramsString
   * @param [loader=false]
   * @param searchFields
   * @description Get the table data from the api
   */
  private _getTableData(
    apiName: string,
    limit?: number,
    offset?: number,
    prefix: string = "item",
    paramsString?: string,
    loader: boolean = false,
    searchFields?: SearchFields
  ) {
    if (this.isLocalData()) return;

    if (!this._dataTableDataService[apiName as keyof DataTableDataService]) return;

    searchFields = {
      searchFields: {
        ...searchFields?.searchFields,
        ...this.alwaysFilterBySearchFields().searchFields,
      },
    };

    return this._dataTableDataService[apiName as keyof DataTableDataService](
      limit,
      offset,
      prefix,
      paramsString,
      loader,
      searchFields
    ).subscribe({
      next: (response: MainResponseModel) => {
        if (response.inError) {
          this.tableData.set([]);
          this.totalRows.set(0);
          return;
        }
        this.tableData.set(response.responseData as object[]);
        this.totalRows.set((response.additionalFields as TotalRows).totalOfRows);

        this.tableDataChanged.emit(this.tableData());
      },
      error: () => {
        if (isDevMode() && window.location.hostname === "localhost") {
          // eslint-disable-next-line no-console
          console.log(
            "%c Hey Developer: Error While getting the table data Maybe @Input() is missing or there is another issue",
            "background: #222; color: red; font-size: 1.5em"
          );
        }
      },
    });
  }

  private _exportTable(apiName: string, prefix: string = "item", paramsString?: string, loader: boolean = false) {
    if (!this._dataTableDataService[apiName as keyof DataTableDataService]) return;

    return this._dataTableDataService[apiName as keyof DataTableDataService](
      undefined,
      0,
      prefix,
      paramsString,
      loader,
      { searchFields: {} }
    ).subscribe({
      next: (response: MainResponseModel) => {
        if (response.inError) return;
        this._dataTableExportService.exportTableData(
          this.formCatalog()?.searchKey,
          this._displayedColumns(),
          this.formCatalog()?.parameterCatalogs ?? [],
          response.responseData as object[]
        );
      },
      error: () => {
        if (isDevMode() && window.location.hostname === "localhost") {
          // eslint-disable-next-line no-console
          console.log(
            "%c Hey Developer: Error While getting the table data Maybe @Input() is missing or there is another issue",
            "background: #222; color: red; font-size: 1.5em"
          );
        }
      },
    });
  }

  private _saveTableSettings(reloadTable: boolean = false) {
    this._dataTableService.saveColumnsConfig(this.tableColumns()).subscribe((response: MainResponseModel) => {
      if (!response.inError) {
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        if (reloadTable) {
          this._getConfig();
          this._getTableData(
            this.apiName(),
            this.limit(),
            this.offset(),
            this.apiPrefix(),
            this.paramsString(),
            this.loader(),
            this.searchFields()
          );
        }
      }
    });
  }

  /**
   * Sort Table Data Without Calling API this Used when isLocalData enabled
   * @param event
   */
  private _localSort(event: SortEvent) {
    this.tableDataFiltered.set(this._dataTableService.sortDataLocally(event, this.tableDataFiltered()));
    const arrayCopy = AdaaHelper.clone(this.tableDataFiltered());
    this.offset.set(this.limit() * (this.currentPage() - 1));
    this.tableData.set(arrayCopy.splice(this.offset(), this.limit()));
  }

  /**
   * Filter Table Data Without Calling API this Used when isLocalData enabled
   * @param event
   */
  private _localFilter(event: any): void {
    this.currentFilter.set(event);
    this._applyLocalFilter();
  }

  /**
   * To check the table state on init table component
   * get the filters and the pagination
   */
  private _checkTableState(): void {
    //If we have a default filter don't save the table state
    if (this.staticSearchFields().searchFields && Object.keys(this.staticSearchFields().searchFields).length > 0) {
      this.searchFields.set({
        searchFields: {
          ...this.searchFields().searchFields,
          ...this.staticSearchFields().searchFields,
        },
      });
    } else {
      const state = this._dataTableService.getPageTableState(this._stateConfigKey());
      if (state === undefined) {
        this._setTableState();
      } else {
        this.currentPage.set(state.page);
        this.currentSort.set(state.sort);
        this.currentFilter.set(state.filter);
        this.limit.set(state.limit);
      }

      this.searchFields.set(
        this._dataTableService.getSortAndFilterData(this.tableColumns(), this.currentFilter(), this.currentSort())
      );
    }

    this.offset.set(this.limit() * (this.currentPage() - 1));

    this._getTableData(
      this.apiName(),
      this.limit(),
      this.offset(),
      this.apiPrefix(),
      this.paramsString(),
      this.loader(),
      this.searchFields()
    );
  }

  /**
   * To save the table state to avoid resetting the table sort/pagination/filters
   */
  private _setTableState(): void {
    this._dataTableService.setPageTableState(this._stateConfigKey(), {
      page: this.currentPage(),
      sort: this.currentSort()!,
      filter: this.currentFilter(),
      limit: this.limit(),
    });
  }

  private _applyLocalFilter(): void {
    this.searchFields.set(
      this._dataTableService.getSortAndFilterData(this.tableColumns(), this.currentFilter(), this.currentSort())
    );

    this.tableDataFiltered.set(
      this._dataTableService.filterDataLocally(this.searchFields(), this.tableColumns(), this.tableDataCopy())
    );

    //Reset paginator
    this.totalRows.set(this.tableDataFiltered().length);
    this.currentPage.set(1);
    this.offset.set(this.limit() * (this.currentPage() - 1));
    const arrayCopy = AdaaHelper.clone(this.tableDataFiltered());
    this.tableData.set(arrayCopy.splice(this.offset(), this.limit()));
  }

  private _doPagination(): void {
    this.offset.set(this.limit() * (this.currentPage() - 1));
    if (this.isLocalData()) {
      const arrayCopy = AdaaHelper.clone(this.tableDataFiltered());
      this.tableData.set(arrayCopy.splice(this.offset(), this.limit()));
    } else {
      this._setTableState();
      this._getTableData(
        this.apiName(),
        this.limit(),
        this.offset(),
        this.apiPrefix(),
        this.paramsString(),
        this.loader(),
        this.searchFields()
      );
    }
  }
}
